// ⬡B:ccwa_core.use_draggable:PORT:agui_reference_draggable_20260711⬡
// Ported and adapted from rezaulhreza/jarvis web/src/hooks/useDraggable.ts (studied
// during the AG-UI/GenUI research). Their version is a complete movable-window hook
// with corner snap, viewport clamp, and persistence -- strictly better than the
// ad-hoc face drag. Adapted here: pointer events (touch + mouse), our session-memory
// persistence via a module namespace (window storage is unavailable in artifacts),
// and a corner-or-free position model so SHE can snap it to a named corner while HE
// drags it anywhere. This one hook now powers every floating surface on the glass.
import { useState, useRef, useCallback } from 'react';

const _mem = (typeof window !== 'undefined' && (window.__ANU_DRAG = window.__ANU_DRAG || {})) || {};

export function useDraggable({ storageKey, defaultCorner = 'top-right', width = 300, height = 300, snap = 22 } = {}) {
  const saved = storageKey ? _mem[storageKey] : null;
  const [pos, setPos] = useState(saved || { x: null, y: null, corner: defaultCorner });
  const drag = useRef(null);

  const persist = useCallback((p) => { if (storageKey) _mem[storageKey] = p; }, [storageKey]);

  const toCorner = useCallback((corner) => {
    const p = { x: null, y: null, corner }; setPos(p); persist(p);
  }, [persist]);

  const onPointerDown = useCallback((e) => {
    if (e.target.closest('button') || e.target.closest('a')) return;
    const r = e.currentTarget.getBoundingClientRect();
    drag.current = { dx: e.clientX - r.left, dy: e.clientY - r.top };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (er) {}
  }, []);

  const onPointerMove = useCallback((e) => {
    const d = drag.current; if (!d) return;
    let x = e.clientX - d.dx, y = e.clientY - d.dy;
    // viewport clamp
    x = Math.max(6, Math.min(window.innerWidth - width - 6, x));
    y = Math.max(60, Math.min(window.innerHeight - 120, y));
    // edge snap
    if (x < snap) x = 8; else if (x + width > window.innerWidth - snap) x = window.innerWidth - width - 8;
    const p = { x, y, corner: null }; setPos(p); persist(p);
  }, [width, snap, persist]);

  const onPointerUp = useCallback((e) => {
    drag.current = null; try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (er) {}
  }, []);

  const style = (() => {
    const base = { position: 'fixed', zIndex: 60, cursor: 'grab', touchAction: 'none' };
    if (pos.x != null) return Object.assign(base, { left: pos.x, top: pos.y });
    const c = pos.corner || defaultCorner;
    return Object.assign(base,
      c === 'top-left' ? { left: 18, top: 74 } :
      c === 'bottom-left' ? { left: 18, bottom: 96 } :
      c === 'bottom-right' ? { right: 18, bottom: 96 } :
      c === 'center' ? { left: '50%', top: '38%', transform: 'translate(-50%, -50%)' } :
      { right: 18, top: 74 });
  })();

  return { style, toCorner, dragHandlers: { onPointerDown, onPointerMove, onPointerUp } };
}

export default useDraggable;
